import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Download, RefreshCw, ArrowLeft, Inbox, Play, Square, RotateCw, Plus, Trash2, KeyRound, Check, X } from "lucide-react";
import { API, LogoMark } from "@/lib/site";
import ChatTab from "@/components/admin/ChatTab";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "in_review", label: "In Review" },
  { value: "proposal_sent", label: "Proposal Sent" },
  { value: "closed", label: "Closed" },
];

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
  contacted: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  in_review: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-400",
  proposal_sent: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400",
  closed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
};

const DOT = { running: "bg-emerald-500", up: "bg-emerald-500", offline: "bg-slate-400", down: "bg-red-500", provisioning: "bg-amber-500 animate-pulse", unknown: "bg-slate-300" };

const inputCls =
  "px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60";

const TABS = ["overview", "leads", "chat", "customers", "servers", "orders", "sites", "mailboxes"];

function useAdminApi(token) {
  const headers = { Authorization: `Bearer ${token}` };
  return {
    headers,
    get: (path, params) => axios.get(`${API}${path}`, { headers, params }),
    post: (path, body, params) => axios.post(`${API}${path}`, body, { headers, params }),
    patch: (path, body) => axios.patch(`${API}${path}`, body, { headers }),
    del: (path) => axios.delete(`${API}${path}`, { headers }),
  };
}

function LeadsTab({ api }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/leads", filter ? { status: filter } : {});
    setLeads(data.leads);
    setStats(data.stats);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load().catch(() => toast.error("Failed to load leads.")); }, [load]);

  const updateStatus = async (leadId, status) => {
    try {
      const { data } = await api.patch(`/leads/${leadId}`, { status });
      setLeads((ls) => ls.map((l) => (l.lead_id === leadId ? data : l)));
      toast.success("Lead status updated.");
    } catch { toast.error("Could not update status."); }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/leads/export`, { headers: api.headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "wynova-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  return (
    <div>
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[["Total Leads", stats.total], ["New", stats.new], ["Contacted", stats.contacted], ["Closed", stats.closed]].map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
              <p className="mt-2 font-display font-black text-3xl">{value}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-lead-status-filter" className={inputCls}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button onClick={exportCsv} data-testid="admin-export-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-medium hover:border-primary hover:text-primary transition-colors">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      {leads.length === 0 ? (
        <div data-testid="admin-leads-empty" className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-16 text-center">
          <Inbox className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600" />
          <p className="mt-4 text-sm text-slate-500">No leads yet.</p>
        </div>
      ) : (
        <div data-testid="admin-lead-table" className="space-y-4">
          {leads.map((l) => (
            <div key={l.lead_id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 sm:p-6">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 justify-between">
                <button onClick={() => setExpanded(expanded === l.lead_id ? null : l.lead_id)} data-testid={`lead-row-${l.lead_id}`} className="text-left min-w-0">
                  <p className="font-semibold truncate">{l.name} <span className="font-normal text-slate-500">· {l.email}</span></p>
                  <p className="text-xs text-slate-500 mt-1">{l.company || "No company"} · {l.service_interest} · {l.budget_range} · {new Date(l.created_at).toLocaleString()}</p>
                </button>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_COLORS[l.status] || ""}`}>{STATUSES.find((s) => s.value === l.status)?.label || l.status}</span>
                  <select value={l.status} onChange={(e) => updateStatus(l.lead_id, e.target.value)} data-testid={`lead-status-select-${l.lead_id}`}
                          className="px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-transparent text-sm">
                    {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              {expanded === l.lead_id && (
                <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Message</p>
                  <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{l.message}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewTab({ api }) {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setO(data)).catch(() => toast.error("Failed to load overview.")); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!o) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  const cards = [
    ["Customers", o.customers], ["Servers", o.servers], ["Servers Running", o.servers_running],
    ["Orders", o.orders], ["Paid Orders", o.orders_paid], ["New Leads", o.leads_new],
    ["Reinstalls Pending", o.reinstalls_pending], ["Mailboxes Pending", o.mailboxes_pending], ["Sites Monitored", o.sites],
  ];
  return (
    <div>
      {o.simulation_mode && (
        <div className="mb-8 rounded-2xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-4 text-sm text-amber-800 dark:text-amber-300" data-testid="admin-simulation-banner">
          Provisioning is in <strong>simulation mode</strong>. Add the Hetzner API token to the backend environment to create real servers.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-overview">
        {cards.map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
            <p className="mt-2 font-display font-black text-3xl">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomersTab({ api }) {
  const [customers, setCustomers] = useState(null);
  useEffect(() => { api.get("/admin/customers").then(({ data }) => setCustomers(data.customers)).catch(() => { toast.error("Failed to load customers."); setCustomers([]); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!customers) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (customers.length === 0) return <p className="text-sm text-slate-500 py-16 text-center" data-testid="admin-customers-empty">No customer accounts yet.</p>;
  return (
    <div className="space-y-3" data-testid="admin-customers-list">
      {customers.map((c) => (
        <div key={c.user_id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-4 flex flex-wrap justify-between gap-3">
          <div>
            <p className="font-semibold">{c.name}</p>
            <p className="text-xs text-slate-500">{c.email}</p>
          </div>
          <p className="text-xs text-slate-400">joined {new Date(c.created_at).toLocaleDateString()}</p>
        </div>
      ))}
    </div>
  );
}

function ServersTab({ api }) {
  const [servers, setServers] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/servers").then(({ data }) => setServers(data.servers)).catch(() => toast.error("Failed to load servers.")), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const power = async (id, action) => {
    setBusy(`${id}-${action}`);
    try {
      const { data } = await api.post(`/admin/servers/${id}/power`, { action });
      setServers((ss) => ss.map((s) => (s.server_id === id ? { ...s, status: data.status } : s)));
      toast.success(`Server ${action} done.`);
    } catch { toast.error("Power action failed."); }
    setBusy("");
  };

  const reinstall = async (id, decision) => {
    setBusy(`${id}-${decision}`);
    try {
      await api.post(`/admin/servers/${id}/reinstall/${decision}`, {});
      toast.success(decision === "approve" ? "Reinstall approved and started." : "Reinstall request rejected.");
      load();
    } catch { toast.error("Action failed."); }
    setBusy("");
  };

  if (!servers) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (servers.length === 0) return <p className="text-sm text-slate-500 py-16 text-center" data-testid="admin-servers-empty">No servers provisioned yet.</p>;
  return (
    <div className="space-y-4" data-testid="admin-servers-list">
      {servers.map((s) => (
        <div key={s.server_id} data-testid={`admin-server-${s.server_id}`}
             className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold">{s.name} <span className="font-normal text-slate-500">· {s.plan_name}</span></p>
              <p className="text-xs text-slate-500 mt-1 font-mono">{s.ip_address || "no ip"} · {s.user_email} · {s.provider}</p>
            </div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
              <span className={`w-2.5 h-2.5 rounded-full ${DOT[s.status] || "bg-slate-400"}`} /> {s.status}
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {["start", "stop", "reboot"].map((a) => (
              <button key={a} onClick={() => power(s.server_id, a)} disabled={!!busy} data-testid={`admin-server-${a}-${s.server_id}`}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-slate-500/10 text-slate-600 dark:text-slate-300 text-xs font-bold hover:bg-slate-500/20 disabled:opacity-40 transition-colors">
                {a === "start" ? <Play className="w-3.5 h-3.5" /> : a === "stop" ? <Square className="w-3.5 h-3.5" /> : <RotateCw className="w-3.5 h-3.5" />} {a}
              </button>
            ))}
            {s.reinstall?.status === "pending" && (
              <span className="inline-flex items-center gap-2 ml-2" data-testid={`admin-reinstall-actions-${s.server_id}`}>
                <span className="text-xs font-bold text-amber-600 dark:text-amber-400">Reinstall requested</span>
                <button onClick={() => reinstall(s.server_id, "approve")} disabled={!!busy} data-testid={`admin-reinstall-approve-${s.server_id}`}
                        className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-40">
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button onClick={() => reinstall(s.server_id, "reject")} disabled={!!busy} data-testid={`admin-reinstall-reject-${s.server_id}`}
                        className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-40">
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function OrdersTab({ api }) {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/admin/orders").then(({ data }) => setOrders(data.orders)).catch(() => { toast.error("Failed to load orders."); setOrders([]); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!orders) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (orders.length === 0) return <p className="text-sm text-slate-500 py-16 text-center" data-testid="admin-orders-empty">No orders yet.</p>;
  return (
    <div className="space-y-3" data-testid="admin-orders-list">
      {orders.map((o) => (
        <div key={o.session_id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-4 flex flex-wrap justify-between items-center gap-3">
          <div>
            <p className="font-semibold">{o.plan_name} <span className="font-normal text-slate-500">· {o.user_email}</span></p>
            <p className="text-xs text-slate-500 mt-1">{new Date(o.created_at).toLocaleString()} · {o.server_id ? "server deployed" : "no server"}</p>
          </div>
          <div className="flex items-center gap-5">
            <span className="font-display font-bold">${o.amount?.toFixed(2)} <span className="text-xs font-normal uppercase text-slate-400">{o.currency}</span></span>
            <span className={`text-xs font-bold uppercase tracking-wider ${o.payment_status === "paid" ? "text-emerald-500" : "text-amber-500"}`}>{o.payment_status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function SitesTab({ api }) {
  const [sites, setSites] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/sites").then(({ data }) => setSites(data.sites)).catch(() => { toast.error("Failed to load sites."); setSites([]); });
    api.get("/admin/customers").then(({ data }) => {
      setCustomers(data.customers);
      if (data.customers.length) setUserId((u) => u || data.customers[0].user_id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!userId) return toast.error("No customer selected.");
    setBusy(true);
    try {
      await api.post("/admin/sites", { name, url }, { user_id: userId });
      toast.success("Site added and first check started.");
      setName(""); setUrl("");
      setTimeout(load, 3000);
    } catch { toast.error("Could not add site."); }
    setBusy(false);
  };

  const remove = async (id) => {
    try {
      await api.del(`/admin/sites/${id}`);
      setSites((ss) => ss.filter((s) => s.site_id !== id));
      toast.success("Site removed.");
    } catch { toast.error("Could not remove site."); }
  };

  if (!sites) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  return (
    <div>
      <form onSubmit={add} className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 mb-8 flex flex-col lg:flex-row gap-4">
        <select value={userId} onChange={(e) => setUserId(e.target.value)} className={`${inputCls} lg:w-56`} data-testid="admin-site-customer-select">
          {customers.map((c) => <option key={c.user_id} value={c.user_id}>{c.email}</option>)}
        </select>
        <input required minLength={2} placeholder="Site name" value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} flex-1`} data-testid="admin-site-name" />
        <input required minLength={4} placeholder="https://clientdomain.com" value={url} onChange={(e) => setUrl(e.target.value)} className={`${inputCls} flex-1`} data-testid="admin-site-url" />
        <button type="submit" disabled={busy || !customers.length} data-testid="admin-site-add"
                className="inline-flex justify-center items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary text-white font-semibold text-sm hover:bg-blue-700 disabled:opacity-60 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Site
        </button>
      </form>
      {sites.length === 0 ? (
        <p className="text-sm text-slate-500 py-16 text-center" data-testid="admin-sites-empty">No sites monitored yet.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-sites-list">
          {sites.map((s) => (
            <div key={s.site_id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold truncate">{s.name} <span className="font-normal text-slate-500">· {s.user_email}</span></p>
                <p className="text-xs text-slate-500 font-mono truncate">{s.url} · {s.response_ms != null ? `${s.response_ms}ms` : "—"} · {s.last_checked_at ? new Date(s.last_checked_at).toLocaleTimeString() : "not checked"}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                  <span className={`w-2.5 h-2.5 rounded-full ${DOT[s.status] || "bg-slate-400"}`} /> {s.status}
                </span>
                <button onClick={() => remove(s.site_id)} aria-label="Remove site" data-testid={`admin-site-delete-${s.site_id}`}
                        className="p-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-500/10 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MailboxesTab({ api }) {
  const [boxes, setBoxes] = useState(null);
  const [passwords, setPasswords] = useState({});
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/mailboxes").then(({ data }) => setBoxes(data.mailboxes)).catch(() => { toast.error("Failed to load mailboxes."); setBoxes([]); }), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const provision = async (id) => {
    const password = passwords[id];
    if (!password || password.length < 8) return toast.error("Set a password of at least 8 characters first.");
    setBusy(id);
    try {
      await api.post(`/admin/mailboxes/${id}/provision`, { password });
      toast.success("Mailbox provisioned — credentials are now visible to the customer.");
      load();
    } catch { toast.error("Provisioning failed."); }
    setBusy("");
  };

  if (!boxes) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (boxes.length === 0) return <p className="text-sm text-slate-500 py-16 text-center" data-testid="admin-mailboxes-empty">No mailbox requests yet.</p>;
  return (
    <div className="space-y-4" data-testid="admin-mailboxes-list">
      {boxes.map((m) => (
        <div key={m.mailbox_id} data-testid={`admin-mailbox-${m.mailbox_id}`}
             className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-sm font-semibold truncate">{m.address}</p>
            <p className="text-xs text-slate-500 mt-1">{m.user_email} · requested {new Date(m.created_at).toLocaleString()}</p>
          </div>
          {m.status === "provisioned" ? (
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-500">Provisioned</span>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <input type="text" minLength={8} placeholder="Set mailbox password" value={passwords[m.mailbox_id] || ""}
                     onChange={(e) => setPasswords((p) => ({ ...p, [m.mailbox_id]: e.target.value }))}
                     className={`${inputCls} w-56`} data-testid={`admin-mailbox-password-${m.mailbox_id}`} />
              <button onClick={() => provision(m.mailbox_id)} disabled={busy === m.mailbox_id} data-testid={`admin-mailbox-provision-${m.mailbox_id}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-primary text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {busy === m.mailbox_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Provision
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("wynova_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const api = useAdminApi(token);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/me`, { headers: api.headers })
      .then(({ data }) => {
        if (data.role !== "admin") throw new Error("not admin");
      })
      .catch(() => { localStorage.removeItem("wynova_admin_token"); setToken(""); });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (e) => {
    e.preventDefault();
    setAuthError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.user.role !== "admin") {
        setAuthError("This account is not an admin.");
        return;
      }
      localStorage.setItem("wynova_admin_token", data.token);
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name}.`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setAuthError(typeof d === "string" ? d : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen technical-grid bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4">
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 shadow-2xl shadow-slate-900/10">
          <div className="flex items-center gap-3 mb-8">
            <LogoMark className="w-10 h-10" />
            <div>
              <h1 className="font-display font-bold text-lg tracking-tight">Wynova Admin</h1>
              <p className="text-xs text-slate-500">Operations console</p>
            </div>
          </div>
          <input type="email" required placeholder="Admin email" value={email} data-testid="admin-login-email"
                 onChange={(e) => setEmail(e.target.value)}
                 className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/60" />
          <input type="password" required placeholder="Password" value={password} data-testid="admin-login-password"
                 onChange={(e) => setPassword(e.target.value)}
                 className="w-full mt-4 px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/60" />
          {authError && <p data-testid="admin-login-error" className="mt-3 text-sm text-red-500">{authError}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit"
                  className="mt-6 w-full inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full bg-primary text-white font-semibold text-sm hover:bg-blue-700 disabled:opacity-60 transition-colors">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
          </button>
          <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-primary">
            <ArrowLeft className="w-3 h-3" /> Back to website
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark className="w-8 h-8" />
            <span className="font-display font-bold tracking-tight">Wynova Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => window.location.reload()} data-testid="admin-refresh-btn" aria-label="Refresh"
                    className="p-2 rounded-full border border-slate-300 dark:border-slate-700 hover:border-primary hover:text-primary transition-colors">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={() => { localStorage.removeItem("wynova_admin_token"); setToken(""); }} data-testid="admin-logout-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium hover:opacity-90">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
                    className={`px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap capitalize transition-colors ${
                      tab === t ? "border-primary text-primary" : "border-transparent text-slate-500 dark:text-slate-400 hover:text-foreground"
                    }`}>
              {t}
            </button>
          ))}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8 capitalize">{tab}</h1>
        {tab === "overview" && <OverviewTab api={api} />}
        {tab === "leads" && <LeadsTab api={api} />}
        {tab === "chat" && <ChatTab api={api} />}
        {tab === "customers" && <CustomersTab api={api} />}
        {tab === "servers" && <ServersTab api={api} />}
        {tab === "orders" && <OrdersTab api={api} />}
        {tab === "sites" && <SitesTab api={api} />}
        {tab === "mailboxes" && <MailboxesTab api={api} />}
      </main>
    </div>
  );
}
